import type { ImageLoader, ImageLoaderProps } from 'next/image'

// Hosts fronted by Akamai Image & Video Manager, which resizes + format-negotiates at the
// edge via the `imwidth` param — so Next's on-pod `/_next/image` optimizer can be skipped.
const IVM_HOSTS = new Set([
    'images.traum-ferienwohnungen.de',
    'img2.traum-ferienwohnungen.de',
    'static2.traum-ferienwohnungen.de',
])

// True when `src` is an absolute URL on an IVM host (safe to serve straight from the edge).
export const isAkamaiOptimizable = (src: string): boolean => {
    try {
        return IVM_HOSTS.has(new URL(src).hostname)
    } catch {
        return false
    }
}

// Serves the image from Akamai IVM at a caller-supplied fixed width, rather than whatever
// width next/image would otherwise compute from `sizes`/`deviceSizes`. `next/image` still
// calls this once per candidate width it has already decided on (from `getWidths()`), so the
// caller is responsible for keeping the component's `sizes`/`width`/`height` props consistent
// with a single fixed size — otherwise the resulting srcset ends up with multiple entries
// pointing at the same `imwidth`, mislabeled with different `w` descriptors.
// Without a fixed `imwidth`, falls back to next/image's own computed `width` — still skipping
// the on-pod optimizer, just without a pinned size.
export const createAkamaiImageLoader =
    (imwidth?: number): ImageLoader =>
    ({ src, width }: ImageLoaderProps): string => {
        const url = new URL(src)
        url.searchParams.set('imwidth', String(imwidth ?? width))
        return url.toString()
    }
